import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import {
  Bot, AlertTriangle, CheckCircle2, RefreshCw, Play, Pause, ShieldAlert,
  Zap, Server, Clock, Users as UsersIcon, Folder, Activity, X
} from 'lucide-react';

const SEVERITY_STYLE = {
  critical: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-500' },
  high:     { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-500' },
  medium:   { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', dot: 'bg-blue-500' },
  low:      { bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-500/30', dot: 'bg-zinc-500' },
};

const CATEGORY_ICON = {
  infrastructure: Server,
  production: Activity,
  compliance: ShieldAlert,
};

export default function BotsPage() {
  const { token } = useAuth();
  const api = useMemo(() => axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const [summary, setSummary] = useState(null);
  const [bots, setBots] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [showResolved, setShowResolved] = useState(false);
  const [filterBot, setFilterBot] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, b, a] = await Promise.all([
        api.get('/bots/summary'),
        api.get('/bots'),
        api.get(`/bots/alerts?resolved=${showResolved}${filterBot ? `&bot_code=${filterBot}` : ''}`),
      ]);
      setSummary(s.data);
      setBots(b.data.bots || []);
      setAlerts(a.data.alerts || []);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [api, showResolved, filterBot]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const runBot = async (code) => {
    setRunning(code);
    try { await api.post(`/bots/${code}/run`); } catch (e) { /* noop */ }
    setRunning(null);
    load();
  };

  const toggleBot = async (code) => {
    try { await api.post(`/bots/${code}/toggle`); } catch (e) { /* noop */ }
    load();
  };

  const resolveAlert = async (id) => {
    try { await api.post(`/bots/alerts/${id}/resolve`); } catch (e) { /* noop */ }
    load();
  };

  if (loading) {
    return <div className="p-8 text-zinc-500 text-sm">Loading VawworldOS Bots...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="bots-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-sm flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold text-white">Mission-Critical Bots</h1>
            <p className="text-xs text-zinc-400">Autonomous watchdogs monitoring every critical point of VawworldOS</p>
          </div>
        </div>
        <button onClick={load} data-testid="bots-refresh-btn"
          className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-sm flex items-center gap-1.5">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Autonomous mode banner */}
      <div data-testid="autonomous-banner" className="bg-gradient-to-r from-emerald-600/15 via-cyan-600/10 to-transparent border border-emerald-500/30 rounded-sm px-4 py-3 flex items-center gap-3">
        <div className="relative">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
          <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping opacity-60" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Autonomous Mode · Live</span>
            <span className="text-[10px] text-zinc-500 font-mono">scheduler tick: 30s · 24×7</span>
          </div>
          <p className="text-[11px] text-zinc-300 mt-0.5 leading-snug">
            All bots run themselves on their own schedules — the studio never waits for a human to click anything. "Run Now" is a manual override for admins who want an immediate re-scan (e.g., right after fixing a blocker).
          </p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Bot} label="Total Bots" value={summary.total_bots} color="text-cyan-400" />
          <StatCard icon={Zap} label="Active" value={summary.active_bots} color="text-emerald-400" />
          <StatCard icon={AlertTriangle} label="Critical" value={summary.critical} color="text-red-400" accent="red" />
          <StatCard icon={AlertTriangle} label="High" value={summary.high} color="text-amber-400" accent="amber" />
          <StatCard icon={AlertTriangle} label="Medium" value={summary.medium} color="text-blue-400" accent="blue" />
        </div>
      )}

      {/* Bot grid */}
      <div>
        <h2 className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2">Deployed Bots</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {bots.map(b => {
            const CatIcon = CATEGORY_ICON[b.category] || Bot;
            const sev = SEVERITY_STYLE[b.severity] || SEVERITY_STYLE.low;
            const last = b.last_run_at ? new Date(b.last_run_at) : null;
            const ageMin = last ? Math.floor((Date.now() - last.getTime()) / 60000) : null;
            const nextMin = last ? Math.max(0, (b.schedule_minutes || 60) - ageMin) : 0;
            return (
              <div key={b.code} data-testid={`bot-card-${b.code}`}
                className={`bg-zinc-900 border ${b.is_active ? 'border-zinc-800' : 'border-zinc-800/40 opacity-60'} rounded-sm p-4 flex flex-col gap-2`}>
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-sm ${sev.bg} ${sev.border} border flex items-center justify-center flex-shrink-0`}>
                    <CatIcon className={`w-4 h-4 ${sev.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${sev.dot} animate-pulse`} />
                      <h3 className="text-sm font-semibold text-zinc-100 truncate">{b.name}</h3>
                    </div>
                    <p className="text-[10px] font-mono text-zinc-600">{b.code}</p>
                  </div>
                  <span className={`text-[9px] uppercase tracking-wider ${sev.text} font-semibold`}>{b.severity}</span>
                </div>
                <p className="text-[11px] text-zinc-400 leading-relaxed">{b.mission}</p>

                {/* Autonomous schedule panel — primary signal */}
                <div className={`rounded-sm px-2.5 py-2 border ${b.is_active ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-zinc-800/40 border-zinc-800'} mt-1`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-[10px]">
                      {b.is_active ? (
                        <>
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                          </span>
                          <span className="text-emerald-300 font-semibold uppercase tracking-wider">Auto · Live</span>
                        </>
                      ) : (
                        <><Pause className="w-3 h-3 text-zinc-500" /><span className="text-zinc-500 uppercase tracking-wider font-semibold">Paused</span></>
                      )}
                    </span>
                    <span className={`text-[9px] font-mono ${(b.open_alerts || 0) > 0 ? 'text-red-400 font-bold' : 'text-emerald-500'}`}>
                      {b.open_alerts || 0} open
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-zinc-400 mt-1.5">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-zinc-500" /> every <span className="text-zinc-200 font-semibold">{b.schedule_minutes}m</span></span>
                    <span className="text-zinc-500">{ageMin !== null ? `last ${ageMin}m ago` : 'warming up'}</span>
                    {b.is_active && last && (
                      <span className="text-cyan-300">next in <span className="font-semibold">{nextMin}m</span></span>
                    )}
                  </div>
                </div>

                {/* Controls row — manual override, visually de-emphasized */}
                <div className="flex items-center justify-between gap-1.5 mt-1 pt-1.5 border-t border-zinc-800/60">
                  <span className="text-[9px] text-zinc-600 uppercase tracking-wider">Manual Override</span>
                  <div className="flex gap-1">
                    <button data-testid={`bot-run-${b.code}`} onClick={() => runBot(b.code)} disabled={running === b.code}
                      title="Force an immediate scan (bot will still run on its own schedule anyway)"
                      className="h-6 px-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-300 text-[10px] rounded-sm flex items-center gap-1">
                      <Play className="w-2.5 h-2.5" /> {running === b.code ? '...' : 'Force scan'}
                    </button>
                    <button data-testid={`bot-toggle-${b.code}`} onClick={() => toggleBot(b.code)}
                      title={b.is_active ? 'Pause autonomous scheduler for this bot' : 'Resume autonomous scheduler for this bot'}
                      className={`w-6 h-6 rounded-sm flex items-center justify-center ${b.is_active ? 'bg-zinc-800 hover:bg-red-500/20 text-zinc-400 hover:text-red-400' : 'bg-emerald-600/20 hover:bg-emerald-500 text-emerald-400 hover:text-white'}`}>
                      {b.is_active ? <Pause className="w-2.5 h-2.5" /> : <Play className="w-2.5 h-2.5" />}
                    </button>
                    <button data-testid={`bot-filter-${b.code}`} onClick={() => setFilterBot(filterBot === b.code ? '' : b.code)}
                      title="Filter alert feed below to this bot"
                      className={`w-6 h-6 rounded-sm flex items-center justify-center ${filterBot === b.code ? 'bg-cyan-600 text-white' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'}`}>
                      <AlertTriangle className="w-2.5 h-2.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Alerts feed */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[10px] text-zinc-500 uppercase tracking-widest">
            Alerts {filterBot && <span className="text-cyan-400">filtered by {filterBot}</span>}
          </h2>
          <div className="flex gap-1.5">
            {filterBot && (
              <button onClick={() => setFilterBot('')}
                className="h-6 px-2 bg-zinc-800 text-zinc-400 text-[10px] rounded-sm">Clear filter</button>
            )}
            <button data-testid="toggle-resolved-btn" onClick={() => setShowResolved(!showResolved)}
              className={`h-6 px-2 text-[10px] rounded-sm ${showResolved ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
              {showResolved ? 'Showing resolved' : 'Show resolved'}
            </button>
          </div>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm divide-y divide-zinc-800/60">
          {alerts.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm text-zinc-400">All clear. No {showResolved ? 'resolved' : 'open'} alerts{filterBot ? ` for ${filterBot}` : ''}.</p>
            </div>
          ) : alerts.map(a => {
            const sev = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.low;
            return (
              <div key={a.id} data-testid={`alert-${a.id}`} className="px-4 py-3 flex items-start gap-3 hover:bg-zinc-800/20">
                <span className={`mt-1 w-2 h-2 rounded-full ${sev.dot} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-semibold uppercase tracking-wider ${sev.text}`}>{a.severity}</span>
                    <span className="text-[9px] font-mono text-zinc-600">{a.bot_code}</span>
                    {a.occurrences > 1 && <span className="text-[9px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded-sm">×{a.occurrences}</span>}
                    <span className="text-[9px] text-zinc-600 ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-zinc-200 mt-1 leading-snug">{a.message}</p>
                  {a.payload?.project_id && <p className="text-[9px] font-mono text-zinc-600 mt-1">project: {a.payload.project_id}</p>}
                </div>
                {!a.resolved && (
                  <button data-testid={`resolve-${a.id}`} onClick={() => resolveAlert(a.id)}
                    className="h-6 px-2 bg-emerald-600/20 hover:bg-emerald-600 text-emerald-400 hover:text-white text-[10px] rounded-sm flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Resolve
                  </button>
                )}
                {a.resolved && <span className="text-[10px] text-emerald-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> resolved</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, accent }) {
  const accentBg = { red: 'bg-red-500/5 border-red-500/20', amber: 'bg-amber-500/5 border-amber-500/20', blue: 'bg-blue-500/5 border-blue-500/20' };
  return (
    <div className={`bg-zinc-900 border border-zinc-800 ${accent ? accentBg[accent] : ''} rounded-sm p-3 flex items-center gap-3`}>
      <Icon className={`w-5 h-5 ${color}`} />
      <div>
        <div className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</div>
        <div className="text-lg font-bold text-white">{value}</div>
      </div>
    </div>
  );
}
